import type { PageEntry, Node, Entry } from 'contensis-delivery-api';
import dynamic from 'next/dynamic';
import { useTheme } from 'styled-components';

import defaultProfileImage from 'Public/images/cards/profile/default.png';

import cmsParser from 'Common/util/cmsParser';
import { canvasGraduateStoryPage } from 'Common/util/cmsCanvas';

import Card from 'Components/Card';
import type { GraduateStoriesInnerProps } from 'Modules/HeroBanner/MakeWaves/subcomponents/GraduateStoriesInner';

import { colours } from '../Listing/Listing.styled';

import type { Props } from './Story';

export type PageData = {
    entry: PageEntry;
    relatedCourses: Entry[];
    moreStories: Node[];
    ancestors: Node[];
};

const datamap = (data: PageData): Props => {
    const { entry, relatedCourses, moreStories, ancestors } = data;

    const {
        title,
        sys,
        subject,
        student,
        description,
        // heroBanner
        heroBannerImage,
        heroBannerVideo,
        // canvas
        canvas,
        // calls to action
        cTAs,
        // more stories
        moreStoriesHeading
    } = entry;

    const { colors } = useTheme();

    const hero: object = {};
    const inner: GraduateStoriesInnerProps = {
        heading: `${student.person.forename} ${student.person.surname}`,
        videoCaption: description ? description : undefined,
        studentDetails: {
            jobTitle: student.currentRole ? student.currentRole : undefined,
            employer: student.company ? student.company : undefined,
            courseStudied:
                student.course && student.course.degreeType
                    ? `${student.course.degreeType} ${student.course.name}`
                    : student.course
                    ? `${student.course.name}`
                    : undefined
        }
    };
    if (heroBannerVideo) {
        hero['video'] = {
            inner: inner,
            video: cmsParser.transcriptEmbed(heroBannerVideo)
        };
    } else {
        hero['image'] = {
            inner: inner,
            image: cmsParser.image(heroBannerImage)
        };
    }

    return {
        breadcrumbs: cmsParser.breadcrumbs([...ancestors, { entry: { title: title }, id: sys.id }]),
        heroBanner: hero,
        canvasContent: canvas && canvas.length > 0 ? canvasGraduateStoryPage(canvas) : undefined,
        shareLinks: {
            heading: `Share ${student.person.forename}'s story`,
            socialIcons: {
                x: `https://twitter.com/intent/tweet?url=https%3A%2F%2Fwww.solent.ac.uk${sys.uri}&text=`,
                facebook: `https://www.facebook.com/sharer/sharer.php?u=https%3A%2F%2Fwww.solent.ac.uk${sys.uri}`
            }
        },
        cTAs:
            cTAs.length > 0
                ? cTAs.map((cTA, index) => ({
                      ...cmsParser.linkButton(cTA),
                      icon: dynamic(() =>
                          import('@solent-university/solent-icons').then(
                              icons => icons[cTA.icon.name]
                          )
                      ),
                      variant:
                          index === 0
                              ? 'accentThirteenSolid'
                              : index === 1
                              ? 'accentFifteenSolid'
                              : 'accentTwentySevenSolid',
                      id: `story-call-to-action-button-${index}`
                  }))
                : undefined,
        courseStudiedCard:
            student.course && student.course.sys.uri
                ? {
                      image: cmsParser.image(student.course.courseCardImage),
                      heading: student.course.degreeType
                          ? `${student.course.degreeType} ${student.course.name}`
                          : `${student.course.name}`,
                      headingType: 'h3',
                      headingColour: colors.secondary.hex,
                      description: student.course.courseDescription,
                      link: {
                          variant: 'secondary',
                          label: 'Find out more',
                          href: student.course.sys.uri
                      },
                      backgroundColour: colors.secondary.tints[12].hex,
                      includeInnerPadding: true
                  }
                : undefined,
        relatedCourseCards:
            relatedCourses.length > 0
                ? {
                      carousel: {
                          id: 'related-courses-card-carousel',
                          slides: relatedCourses.map(card => ({
                              id: card.sys.id,
                              element: (
                                  <Card
                                      image={cmsParser.image(card.courseCardImage)}
                                      heading={
                                          card.degreeType && card.degreeType !== 'none'
                                              ? `${card.degreeType} ${card.name}`
                                              : card.name
                                      }
                                      description={card.courseDescription}
                                      link={{
                                          label: 'Find out more',
                                          ariaLabel: `Find out more about ${card.name}`,
                                          href: card.sys.uri
                                      }}
                                      includeInnerPadding
                                  />
                              )
                          })),
                          slidesConfig: {
                              large: {
                                  slidesToShow: 2,
                                  gap: 1.5
                              },
                              medium: {
                                  slidesToShow: 2.5,
                                  gap: 1
                              },
                              small: {
                                  slidesToShow: 1.5,
                                  gap: 1
                              }
                          }
                      }
                  }
                : undefined,
        moreStories:
            moreStories.length > 0
                ? {
                      heading: moreStoriesHeading
                          ? moreStoriesHeading
                          : `More stories from ${subject.name}`,
                      cards: moreStories.map((card, index) => ({
                          id: card.sys.id,
                          featured: false,
                          videoStory: false,
                          heading: `${card.student.person.forename} ${card.student.person.surname}`,
                          subject: subject.name,
                          headingType: 'h3',
                          title: card.student.currentRole ? card.student.currentRole : undefined,
                          organisation: card.student.company ? card.student.company : undefined,
                          minutes: 5,
                          quote: card.courseCardQuote,
                          link: {
                              ariaLabel: `${card.student.person.forename} ${card.student.person.surname}'s graduate story`,
                              href: card.sys.uri
                          },
                          themeColor: colours[index % colours.length],
                          images: {
                              avatar: [
                                  card.listingCardImageDefault
                                      ? cmsParser.image(card.listingCardImageDefault)
                                      : { src: defaultProfileImage, alt: '' },
                                  card.listingCardImageOnHover
                                      ? cmsParser.image(card.listingCardImageOnHover)
                                      : card.listingCardImageDefault
                                      ? cmsParser.image(card.listingCardImageDefault)
                                      : { src: defaultProfileImage, alt: '' }
                              ],
                              featureImage: cmsParser.image(card.cardImage)
                          }
                      }))
                  }
                : undefined
    };
};

export default datamap;
